import { Injectable, NgZone } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

declare var hwcrypto: any;

/**
 * Hwcrypto certificate and signature result interfaces
 */
export interface HwcryptoCertificate {
  hex: string;
  encoded: string;
}

export interface HwcryptoSignature {
  hex: string;
  value: ArrayBuffer;
}

/**
 * Configuration options for hwcrypto operations
 */
export interface HwcryptoConfig {
  hashtype: string;
  backend: string;
  language: string;
  filter: string;
}

/**
 * Error types from hwcrypto library
 */
export enum HwcryptoErrorCode {
  NO_CERTIFICATES = 'no_certificates',
  USER_CANCEL = 'user_cancel',
  INVALID_ARGUMENT = 'invalid_argument',
  NO_IMPLEMENTATION = 'no_implementation',
  NOT_ALLOWED = 'not_allowed',
  TECHNICAL_ERROR = 'technical_error'
}

/**
 * Service for handling USB e-signature operations using hwcrypto.js library.
 *
 * This service provides methods for:
 * - Selecting and getting certificates from smart cards / USB tokens
 * - Signing document hashes using the certificate
 * - Converting between base64 and hexadecimal formats
 *
 * Used by both PAdES (PDF) and CAdES (container) signing components.
 */
@Injectable({
  providedIn: 'root'
})
export class HwcryptoService {

  /** Default configuration for hwcrypto operations */
  private readonly defaultConfig: HwcryptoConfig = {
    hashtype: 'SHA-256',
    backend: 'chrome',
    language: 'tr',
    filter: 'SIGN'
  };

  /** Currently selected certificate */
  private currentCertificate: HwcryptoCertificate | null = null;

  constructor(private ngZone: NgZone) {}

  /**
   * Get the default hwcrypto configuration
   */
  getDefaultConfig(): HwcryptoConfig {
    return { ...this.defaultConfig };
  }

  /**
   * Get the currently selected certificate
   */
  getCurrentCertificate(): HwcryptoCertificate | null {
    return this.currentCertificate;
  }

  /**
   * Set the backend for hwcrypto (e.g., 'chrome', 'npapi')
   * @param backend The backend to use
   * @returns true if successful, false otherwise
   */
  useBackend(backend: string = this.defaultConfig.backend): boolean {
    try {
      const hwcryptoLib = (window as any).hwcrypto;
      if (hwcryptoLib && typeof hwcryptoLib.use === 'function') {
        return hwcryptoLib.use(backend);
      }
      return false;
    } catch (error) {
      console.error('Failed to set hwcrypto backend:', error);
      return false;
    }
  }

  /**
   * Request a certificate from the user's smart card or USB token.
   *
   * @param config Optional configuration overrides
   * @returns Observable that emits the certificate when selected
   */
  getCertificate(config?: Partial<HwcryptoConfig>): Observable<HwcryptoCertificate> {
    const mergedConfig = { ...this.defaultConfig, ...config };

    // Set the backend first
    if (!this.useBackend(mergedConfig.backend)) {
      return throwError(() => new Error('Failed to set hwcrypto backend'));
    }

    return new Observable<HwcryptoCertificate>(observer => {
      const hwcryptoLib = (window as any).hwcrypto;

      if (!hwcryptoLib || typeof hwcryptoLib.getCertificate !== 'function') {
        this.ngZone.run(() => {
          observer.error(new Error('hwcrypto library not available'));
        });
        return;
      }

      hwcryptoLib.getCertificate({
        lang: mergedConfig.language,
        filter: mergedConfig.filter
      }).then(
        (certificate: HwcryptoCertificate) => {
          this.ngZone.run(() => {
            this.currentCertificate = certificate;
            observer.next(certificate);
            observer.complete();
          });
        },
        (error: any) => {
          this.ngZone.run(() => {
            console.error('Certificate selection error:', error?.message || error);
            observer.error(error);
          });
        }
      );
    });
  }

  /**
   * Sign a hash using the provided certificate.
   *
   * @param certificate The certificate to use for signing
   * @param hash Base64-encoded hash to sign
   * @param config Optional configuration overrides
   * @returns Observable that emits the signature when complete
   */
  sign(certificate: HwcryptoCertificate, hash: string, config?: Partial<HwcryptoConfig>): Observable<HwcryptoSignature> {
    const mergedConfig = { ...this.defaultConfig, ...config };

    return new Observable<HwcryptoSignature>(observer => {
      const hwcryptoLib = (window as any).hwcrypto;

      if (!hwcryptoLib || typeof hwcryptoLib.sign !== 'function') {
        this.ngZone.run(() => {
          observer.error(new Error('hwcrypto library not available'));
        });
        return;
      }

      const hexHash = this.base64toHEX(hash);

      hwcryptoLib.sign(
        certificate,
        { type: mergedConfig.hashtype, hex: hexHash },
        { lang: mergedConfig.language }
      ).then(
        (signature: HwcryptoSignature) => {
          this.ngZone.run(() => {
            observer.next(signature);
            observer.complete();
          });
        },
        (error: any) => {
          this.ngZone.run(() => {
            console.error('Signing error:', error?.message || error);
            observer.error(error);
          });
        }
      ).catch((error: any) => {
        this.ngZone.run(() => {
          console.error('Signing catch error:', error);
          observer.error(error);
        });
      });
    });
  }

  /**
   * Convert a base64-encoded string to hexadecimal format.
   * Required by hwcrypto.sign() which expects hex-encoded hashes.
   *
   * @param base64 Base64-encoded string
   * @returns Hexadecimal string
   */
  base64toHEX(base64: string): string {
    try {
      const raw = atob(base64);
      let hex = '';

      for (let i = 0; i < raw.length; i++) {
        const charCode = raw.charCodeAt(i).toString(16);
        hex += charCode.length === 2 ? charCode : '0' + charCode;
      }

      return hex;
    } catch (error) {
      console.error('base64toHEX conversion error:', error);
      throw error;
    }
  }

  /**
   * Clear the currently selected certificate
   */
  clearCertificate(): void {
    this.currentCertificate = null;
  }

  /**
   * Check if hwcrypto library is available
   */
  isAvailable(): boolean {
    const hwcryptoLib = (window as any).hwcrypto;
    return !!hwcryptoLib && typeof hwcryptoLib.getCertificate === 'function';
  }

  /**
   * Get hwcrypto library debug info
   */
  getDebugInfo(): string {
    const hwcryptoLib = (window as any).hwcrypto;
    if (hwcryptoLib && typeof hwcryptoLib.debug === 'function') {
      return hwcryptoLib.debug();
    }
    return 'hwcrypto debug info not available';
  }
}
